import {
  PostgrestClient,
  type PostgrestMaybeSingleResponse,
  type PostgrestResponse,
  type PostgrestSingleResponse,
} from "@supabase/postgrest-js";
import {
  type Database,
  type DatabaseWithOptions,
} from "../../../__definitions__/database-definitions";
import {
  SupabaseService,
  SupabaseServiceView,
} from "../../../server/supabase/base-supabase-service";
import type { UUID } from "../../../utils/uuid";
import type { ChannelName } from "../../channels/types";

export type Account = Database["public"]["Tables"]["account"]["Row"];
export type AccountLimit = Database["public"]["Tables"]["account_limit"]["Row"];

export type AdminAccountLimit = Database["ce_admin"]["Views"]["account_limits"]["Row"];
type AdminAccountLimitKeys = Database["ce_admin"]["Views"]["limit_keys"]["Row"];

const LISTEN_ENTITLEMENT = ["Disabled", "Basic", "Pro"] as const;
export type ListenEntitlement = (typeof LISTEN_ENTITLEMENT)[number];

export function listenEnabled(entitlement: ListenEntitlement | null): boolean {
  return entitlement === "Basic" || entitlement === "Pro";
}

export const ACCOUNT_LIMIT_KEYS = {
  slack_aliases: { type: "number" },
  super_admins: { type: "number" },
  contributors: { type: "number" },
  unlayer_fallback: { type: "number" },
  omit_email_test_indicator: { type: "number" },
  email_design_templates: { type: "number" },
  allow_all_and_upcoming_segment: { type: "boolean" },
  slack_connections: { type: "number" },
  enable_swag: { type: "boolean" },
  enable_e_gifting: { type: "boolean" },
  enable_videography: { type: "boolean" },
  enable_design_services: { type: "boolean" },
  enable_listen: { type: "boolean" },
  enable_survey_ai_analysis: { type: "boolean" },
  design_services: { type: "number" },
  distribution_credit: { type: "number" },
  listen_entitlement: { type: "select", values: LISTEN_ENTITLEMENT },
  enable_tracking_pixel: { type: "boolean" },
  enable_polls: { type: "boolean" },
} as const;

export type AccountLimitKey = keyof typeof ACCOUNT_LIMIT_KEYS;

export class SupabaseAccountService extends SupabaseService<"account"> {
  table = "account" as const;

  async getEnabledChannels(account_id: UUID): Promise<NonNullable<ChannelName>[]> {
    const { data } = await this.client
      .from(this.table)
      .select("enable_slack,enable_teams,enable_twilio")
      .eq("id", account_id)
      .single();

    return [
      ...(data && data.enable_slack ? ["slack" as const] : []),
      "work_email" as const,
      "personal_email" as const,
      ...(data && data.enable_teams ? ["teams" as const] : []),
      ...(data && data.enable_twilio ? ["twilio" as const] : []),
    ];
  }
}

export class SupabaseAccountEmployeeSyncConfigService extends SupabaseService<"account_employee_sync_config"> {
  table = "account_employee_sync_config" as const;
}

export class SupabaseAccountLimitService extends SupabaseService<"account_limit"> {
  table = "account_limit" as const;

  async getKey(key: AccountLimitKey): Promise<PostgrestMaybeSingleResponse<AccountLimit>> {
    return this.log_errors(
      await this.client.from(this.table).select<"*", AccountLimit>().eq("key", key).maybeSingle(),
    );
  }

  async getKeys(keys: AccountLimitKey[]): Promise<PostgrestResponse<AccountLimit>> {
    return this.log_errors(
      await this.client.from(this.table).select<"*", AccountLimit>().in("key", keys),
    );
  }
}

export class SupabaseDesignHuddleCustomBrandService extends SupabaseService<"content_custom_template_brand"> {
  table = "content_custom_template_brand" as const;
}

export class CeAdminSupabaseAccountService {
  client: PostgrestClient<Database, DatabaseWithOptions["options"], "ce_admin">;

  constructor(client: PostgrestClient<Database, DatabaseWithOptions["options"], "ce_admin">) {
    this.client = client;
  }

  async getAccountLimits(): Promise<PostgrestResponse<AdminAccountLimit>> {
    return await this.client.from("account_limits").select<"*", AdminAccountLimit>();
  }

  async getKeys(): Promise<PostgrestResponse<AdminAccountLimitKeys>> {
    return await this.client.from("limit_keys").select<"*", AdminAccountLimitKeys>();
  }

  async setAccountLimits(data: {
    account_id: UUID;
    limits: { [key: string]: number };
    business_name: string;
    usage: "DEMO" | "TESTING" | "CLIENT";
    churned: boolean;
    enable_slack: boolean;
    enable_teams: boolean;
    enable_twilio: boolean;
    enable_work_email: boolean;
    enable_personal_email: boolean;
    industry: string;
    enable_tracking_pixel: boolean;
  }): Promise<PostgrestSingleResponse<undefined>> {
    return this.client.rpc("upsert_account_limits", data);
  }

  async createAccount(
    data: Database["ce_admin"]["Functions"]["create_account"]["Args"],
  ): Promise<PostgrestSingleResponse<string>> {
    return this.client.rpc("create_account", data);
  }
}

export type UpsellServices = Database["public"]["Views"]["upsell_services"]["Row"];
export class SupabaseUpsellServices extends SupabaseServiceView<"upsell_services"> {
  table = "upsell_services" as const;
}
